/*
  Gift Engine — Pipeline (bendis) uyumlu sürüm.

  - Tek hediye kampanyası: shop.metafields.custom.active_gift_campaign
  - Sepet hedefine göre (collection / product / vendor / all) hediye urununu
    otomatik olarak ekler veya cikarir.
  - Pipeline temasinin "theme:cart:change" / "theme:cart:reload" eventleri
    ile tam uyumludur. Alpine ve mini-cart custom elementine bagimli degildir.
*/

class GiftEngine {
  constructor() {
    this.THEME_CHANGE_EVENT = "theme:cart:change";
    this.THEME_RELOAD_EVENT = "theme:cart:reload";
    this.LEGACY_REFRESH_EVENT = "cart:refresh";
    this.INTERNAL_EVENT = "gift:cart:changed";
    this.ACTION_COOLDOWN_MS = 400;

    this.isUpdating = false;
    this.lastActionAt = 0;
    this.lastRunAt = 0;
    this.RUN_DEBOUNCE_MS = 60;
    this.runTimer = null;
  }

  now() {
    return Date.now();
  }

  log(...args) {
    if (window.GIFT_ENGINE_DEBUG) {
      try { console.log("[GiftEngine]", ...args); } catch (e) {}
    }
  }

  cartUrl(suffix) {
    const base = (window.theme && window.theme.routes && window.theme.routes.cart) || "/cart";
    return base + suffix;
  }

  normalizeGidToNumeric(id) {
    if (!id) return "";
    const match = String(id).match(/\/(\d+)$/);
    return match ? match[1] : String(id);
  }

  safeArray(value) {
    return Array.isArray(value) ? value : [];
  }

  async getCart() {
    const response = await fetch(this.cartUrl(".js"), {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    });
    return response.json();
  }

  isGiftItem(item) {
    if (!item || !item.properties) return false;
    return (
      item.properties._gift === "true" ||
      item.properties._gift === true ||
      item.properties._source === "gift_engine"
    );
  }

  findGiftItem(cart, variantId) {
    return (
      this.safeArray(cart.items).find(
        item => this.isGiftItem(item) && String(item.variant_id) === String(variantId)
      ) || null
    );
  }

  getCampaign() {
    return window.GIFT_CAMPAIGN || null;
  }

  getGiftVariantId(campaign) {
    return this.normalizeGidToNumeric(
      campaign?.hediye_urun ||
      campaign?.gift_variant_id ||
      campaign?.gift_variant
    );
  }

  getTiers() {
    const tiers = window.GIFT_CAMPAIGN_TIERS;
    if (!Array.isArray(tiers) || tiers.length === 0) return null;

    return tiers
      .map(t => ({
        min_tutar: Number(t.min_tutar) || 0,
        indirim_yuzdesi: Number(t.indirim_yuzdesi) || 0
      }))
      .sort((a, b) => b.min_tutar - a.min_tutar);
  }

  findActiveTier(tiers, total) {
    return tiers.find(t => total >= t.min_tutar * 100) || null;
  }

  getCartTotal(cart) {
    return this.safeArray(cart.items)
      .filter(item => !this.isGiftItem(item))
      .reduce((sum, item) => sum + item.original_line_price, 0);
  }

  getTargetTotal(cart, campaign, collectionData) {
    const items = this.filterTargetItems(cart, campaign, collectionData);
    return items.reduce((sum, item) => sum + item.original_line_price, 0);
  }

  normalizeMap(map) {
    const output = {};

    Object.keys(map || {}).forEach(productId => {
      output[String(productId)] = this.safeArray(map[productId]).map(id =>
        this.normalizeGidToNumeric(id)
      );
    });

    return output;
  }

  async fetchCollectionData() {
    try {
      const response = await fetch(
        "/?section_id=gift-collection-data",
        { credentials: "same-origin" }
      );
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const script = doc.querySelector("[data-gift-collection-data]");
      if (!script) return null;

      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  isInCollection(item, campaign, map) {
    const productCollections = map[String(item.product_id)] || [];
    const targets = this.safeArray(campaign.target_collections).map(id =>
      this.normalizeGidToNumeric(id)
    );

    return productCollections.some(id => targets.includes(String(id)));
  }

  filterTargetItems(cart, campaign, collectionData) {
    const items = this.safeArray(cart.items).filter(item => !this.isGiftItem(item));

    if (campaign.target_scope === "collection") {
      const targets = this.safeArray(campaign.target_collections);
      // Hedef koleksiyon listesi bossa sepetin tamamini hedefle.
      if (targets.length === 0) return items;
      const map = this.normalizeMap(collectionData || {});
      const matched = items.filter(item => this.isInCollection(item, campaign, map));
      // Otomatik "all" koleksiyonu product.collections'da gozukmedigi icin
      // hicbir item eslesmediyse fallback: tum sepet kalemleri.
      if (matched.length === 0) {
        this.log("collection scope matched 0 items, falling back to full cart");
        return items;
      }
      return matched;
    }

    if (campaign.target_scope === "product") {
      const ids = this.safeArray(campaign.target_products).map(product =>
        this.normalizeGidToNumeric(product?.id || product)
      );
      if (ids.length === 0) return items;
      return items.filter(item => ids.includes(String(item.product_id)));
    }

    if (campaign.target_scope === "vendor") {
      const vendors = this.safeArray(campaign.target_vendors).map(String);
      if (vendors.length === 0) return items;
      return items.filter(item => vendors.includes(String(item.vendor)));
    }

    return items;
  }

  checkCondition(cart, campaign, collectionData) {
    const items = this.filterTargetItems(cart, campaign, collectionData);

    if (campaign.condition_type === "quantity") {
      const quantity = items.reduce((sum, item) => sum + item.quantity, 0);
      return quantity >= Number(campaign.condition_value);
    }

    if (campaign.condition_type === "amount") {
      const total = items.reduce((sum, item) => sum + item.original_line_price, 0);
      return total >= Number(campaign.condition_value) * 100;
    }

    return false;
  }

  fireCartChange(cart) {
    if (!cart) return;
    document.dispatchEvent(new CustomEvent(this.THEME_CHANGE_EVENT, {
      detail: { cart },
      bubbles: true
    }));
    document.dispatchEvent(new CustomEvent(this.THEME_RELOAD_EVENT, {
      bubbles: true
    }));
    document.dispatchEvent(new CustomEvent(this.LEGACY_REFRESH_EVENT, {
      detail: { cart }
    }));
  }

  async refreshAndFire() {
    try {
      const cart = await this.getCart();
      this.fireCartChange(cart);
      return cart;
    } catch (e) {
      this.log("refreshAndFire failed", e);
      return null;
    }
  }

  async addGift(variantId) {
    this.log("addGift", variantId);

    const response = await fetch(this.cartUrl("/add.js"), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties: {
              _gift: "true",
              _source: "gift_engine"
            }
          }
        ]
      })
    });

    if (!response.ok) {
      let errorBody = null;
      try { errorBody = await response.json(); } catch (e) {}
      console.error(
        "[GiftEngine] /cart/add.js failed",
        response.status,
        "variantId sent:", variantId,
        "shopify response:", errorBody
      );
      return null;
    }

    return this.refreshAndFire();
  }

  async removeGift(lineKey) {
    this.log("removeGift", lineKey);

    const response = await fetch(this.cartUrl("/change.js"), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0
      })
    });

    if (!response.ok) {
      let errorBody = null;
      try { errorBody = await response.json(); } catch (e) {}
      console.error(
        "[GiftEngine] /cart/change.js failed",
        response.status,
        "lineKey sent:", lineKey,
        "shopify response:", errorBody
      );
      return null;
    }

    return this.refreshAndFire();
  }

  async run() {
    if (this.now() - this.lastActionAt < this.ACTION_COOLDOWN_MS) return;
    if (this.isUpdating) return;

    const campaign = this.getCampaign();
    if (!campaign) return;

    this.isUpdating = true;

    try {
      const variantId = this.getGiftVariantId(campaign);
      if (!variantId) {
        this.log("missing gift variant id");
        return;
      }

      const tiers = this.getTiers();

      const isCollectionScope = campaign.target_scope === "collection";
      const [cart, collectionData] = await Promise.all([
        this.getCart(),
        isCollectionScope ? this.fetchCollectionData() : Promise.resolve(null)
      ]);

      const giftItem = this.findGiftItem(cart, variantId);

      if (tiers) {
        const total = campaign.kademe_kaynagi === "sepet_toplam"
          ? this.getCartTotal(cart)
          : this.getTargetTotal(cart, campaign, collectionData);

        const activeTier = this.findActiveTier(tiers, total);

        if (activeTier && !giftItem) {
          this.lastActionAt = this.now();
          await this.addGift(variantId);
        } else if (!activeTier && giftItem) {
          this.lastActionAt = this.now();
          await this.removeGift(giftItem.key);
        }
      } else {
        const conditionMet = this.checkCondition(cart, campaign, collectionData);

        if (conditionMet && !giftItem) {
          this.lastActionAt = this.now();
          await this.addGift(variantId);
        } else if (!conditionMet && giftItem) {
          this.lastActionAt = this.now();
          await this.removeGift(giftItem.key);
        }
      }
    } catch (error) {
      this.log("run error", error);
    } finally {
      this.isUpdating = false;
    }
  }

  scheduleRun() {
    if (this.runTimer) return;
    this.runTimer = setTimeout(() => {
      this.runTimer = null;
      this.run();
    }, this.RUN_DEBOUNCE_MS);
  }

  isCartWriteUrl(url) {
    if (typeof url !== "string") return false;
    return url.includes("/cart/add") ||
           url.includes("/cart/change") ||
           url.includes("/cart/update") ||
           url.includes("/cart/clear");
  }

  patchFetch() {
    const originalFetch = window.fetch;
    if (!originalFetch || originalFetch.__giftEnginePatched) return;
    const self = this;

    const patched = async function (...args) {
      const response = await originalFetch.apply(this, args);

      try {
        const url = typeof args[0] === "string" ? args[0] : (args[0] && args[0].url);
        if (self.isCartWriteUrl(url) && !self.isUpdating) {
          document.dispatchEvent(new CustomEvent(self.INTERNAL_EVENT));
        }
      } catch (error) {}

      return response;
    };
    patched.__giftEnginePatched = true;
    window.fetch = patched;
  }

  patchXHR() {
    const proto = XMLHttpRequest.prototype;
    if (proto.__giftEngineOpenPatched) return;
    const originalOpen = proto.open;
    const self = this;

    proto.open = function (...args) {
      this.addEventListener("load", function () {
        try {
          if (self.isCartWriteUrl(args[1]) && !self.isUpdating) {
            document.dispatchEvent(new CustomEvent(self.INTERNAL_EVENT));
          }
        } catch (error) {}
      });

      return originalOpen.apply(this, args);
    };
    proto.__giftEngineOpenPatched = true;
  }

  bindEvents() {
    document.addEventListener(this.THEME_CHANGE_EVENT, () => {
      if (this.isUpdating) return;
      this.scheduleRun();
    });

    document.addEventListener(this.INTERNAL_EVENT, () => {
      if (this.isUpdating) return;
      this.scheduleRun();
    });

    document.addEventListener("theme:cart:init", () => this.scheduleRun());

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        setTimeout(() => this.run(), 200);
      });
    } else {
      setTimeout(() => this.run(), 200);
    }

    window.addEventListener("load", () => {
      setTimeout(() => this.run(), 400);
    });
  }

  init() {
    this.patchFetch();
    this.patchXHR();
    this.bindEvents();
  }
}

(() => {
  "use strict";
  if (window.__giftEngineLoaded) return;
  window.__giftEngineLoaded = true;
  const giftEngine = new GiftEngine();
  window.GiftEngine = giftEngine;
  giftEngine.init();
})();
